/**
 * Self-heal for the "enabled viewport with an empty actor list" state
 * (web-demo-frontend #834).
 *
 * In `VolumeViewport`'s content effect the volume attach
 * (`cornerstone.setVolumesForViewports`) runs inside an abortable serial queue.
 * When the effect re-runs mid-attach (a fresh `studyMetadata` object identity
 * produced by the AI-overlay / saved-mask pipeline settling) the previous run's
 * AbortController fires and the queued attach bails at its `signal.aborted`
 * guard *before* the actor is set. The viewport keeps rendering a prior frame,
 * but `getActors()` is empty — so Cornerstone's `LengthTool` (and the other
 * annotation tools) cannot resolve a target volume (`volumeId` is `undefined`,
 * `getCurrentImageId()` is `null`) and the ruler silently no-ops. This is why
 * measuring fails on the AI/priority soft-tissue series while the plain bone
 * series (no overlay churn) attaches cleanly and measures fine.
 *
 * The abort semantics are otherwise correct (they cancel stale series swaps),
 * so rather than fight the race we heal the observable end-state: if a viewport
 * is live but actor-less and we know which volume it should own, re-attach it.
 */

interface AttachableViewport {
  getActors?: () => unknown[] | undefined;
}

export interface EnsureVolumeAttachedDeps {
  /** Resolve the live Cornerstone viewport for `viewportId` (bound to the engine). */
  getViewport: (viewportId: string) => AttachableViewport | null | undefined;
  /** Attach `volumeInputs` to `viewportIds` (bound to cornerstone + engine). */
  setVolumes: (volumeInputs: { volumeId: string }[], viewportIds: string[]) => Promise<void> | void;
  logger?: { warn: (...args: unknown[]) => void };
}

/**
 * Re-attach `volumeId` to `viewportId` iff the viewport is live but has no
 * actor. Returns `true` when a re-attach was performed, `false` otherwise
 * (already attached, unknown viewport, or nothing to attach). Safe to call
 * repeatedly — it is a no-op once the actor is present.
 */
export async function ensureVolumeAttached(
  deps: EnsureVolumeAttachedDeps,
  viewportId: string | null | undefined,
  volumeId: string | null | undefined,
): Promise<boolean> {
  if (!viewportId || !volumeId) return false;

  const viewport = deps.getViewport(viewportId);
  if (!viewport || typeof viewport.getActors !== "function") return false;

  const actors = viewport.getActors();
  if (actors && actors.length > 0) return false;

  deps.logger?.warn(
    "viewer_viewport_no_actor viewportId=%s volumeId=%s — re-attaching volume (web-demo-frontend#834)",
    viewportId,
    volumeId,
  );
  await deps.setVolumes([{ volumeId }], [viewportId]);
  return true;
}
